import { Component, computed, effect, inject, input, OnInit, viewChild } from '@angular/core';
import { OrganizationSidebarLayout } from '../../../organizations/components/organization-sidebar-layout/organization-sidebar-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import { Button, Skeleton, Alert } from 'excellence-ui';
import {
  SupplierContractsFilter,
  SupplierContractStore,
  getInitialFilter,
} from '../../stores/supplier-contracts.store';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { SupplierContractsGrid } from '../../components/supplier-contracts-grid/supplier-contracts-grid';
import { SupplierContractsTable } from '../../components/supplier-contracts-table/supplier-contracts-table';
import { ParseRequestsStore } from '../../../parse-requests/stores/parse-requests.store';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { CreateParseRequestButton } from '../../../parse-requests/components/create-parse-request-button/create-parse-request-button';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ParseRequestCreatedAlert } from '../../../parse-requests/components/parse-request-created-alert/parse-request-created-alert';
import { ParseRequestsActiveDropdown } from '../../../parse-requests/components/parse-requests-active-dropdown/parse-requests-active-dropdown';
import { SupplierContractsFilterForm } from '../../components/supplier-contracts-filter-form/supplier-contracts-filter-form';
import { NgIcon } from '@ng-icons/core';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { TutorialSupplierContractsPopover } from '../../../tutorials/components/tutorial-supplier-contracts-popover/tutorial-supplier-contracts-popover';
import { VideoTutorialService } from '../../../tutorials/services/video-tutorial.service';
import { SupplierContractContactButton } from '../../components/supplier-contract-contact-button/supplier-contract-contact-button';
import { InfiniteScrollDirective } from '../../../../shared/directives/infinite-scroll';

@Component({
  imports: [
    OrganizationSidebarLayout,
    PageHeader,
    PageTitle,
    Button,
    TranslocoPipe,
    SupplierContractsGrid,
    SupplierContractsTable,
    Skeleton,
    Alert,
    ErrorPage,
    CreateParseRequestButton,
    ParseRequestCreatedAlert,
    ParseRequestsActiveDropdown,
    SupplierContractsFilterForm,
    NgIcon,
    TutorialSupplierContractsPopover,
    SupplierContractContactButton,
    InfiniteScrollDirective,
  ],
  templateUrl: './supplier-contracts-page.html',
})
export class SupplierContractsPage implements OnInit {
  private videoTutorialService = inject(VideoTutorialService);
  private organizationsStore = inject(OrganizationsStore);
  private supplierContractStore = inject(SupplierContractStore);
  private parseRequestsStore = inject(ParseRequestsStore);
  private activatedRoute = inject(ActivatedRoute);
  protected router = inject(Router);

  organizationId = input.required<string>();

  private tutorialPopover = viewChild(TutorialSupplierContractsPopover);

  protected parseRequestCreatedId = toSignal(
    this.activatedRoute.queryParamMap.pipe(map((params) => params.get('parseRequestCreated'))),
  );

  supplierContracts = this.supplierContractStore.pages;
  showPreviousYearsAlert = computed(() => {
    if (this.supplierContractStore.isLoading()) {
      return false;
    }
    const year = +this.supplierContractStore.filter().data.year;
    const currentYear = new Date().getFullYear();
    if (year !== currentYear) {
      return false;
    }
    const totalInCurrentYear = this.supplierContractStore.total();
    const total =
      this.organizationsStore.entities()[this.organizationId()]?.childStats?.data
        ?.totalSupplierContracts ?? 0;
    const years = this.supplierContractStore.years() ?? [];
    return (
      total > totalInCurrentYear &&
      totalInCurrentYear === 0 &&
      years.some((year) => year < currentYear)
    );
  });
  showFutureYearsAlert = computed(() => {
    if (this.supplierContractStore.isLoading()) {
      return false;
    }
    const year = +this.supplierContractStore.filter().data.year;
    const currentYear = new Date().getFullYear();
    if (year !== currentYear) {
      return false;
    }
    const totalInCurrentYear = this.supplierContractStore.total();
    const total =
      this.organizationsStore.entities()[this.organizationId()]?.childStats?.data
        ?.totalSupplierContracts ?? 0;
    const years = this.supplierContractStore.years() ?? [];
    return (
      total > totalInCurrentYear &&
      totalInCurrentYear === 0 &&
      years.some((year) => year > currentYear)
    );
  });

  protected view = computed(() => this.supplierContractStore.filter().view);
  protected fullPrice = computed(() => this.supplierContractStore.filter().fullPrice);
  protected year = computed(() => +this.supplierContractStore.filter().data.year);
  protected orderBy = computed(() => this.supplierContractStore.filter().data.orderBy ?? undefined);
  protected orderDirection = computed(
    () => this.supplierContractStore.filter().data.orderDirection ?? undefined,
  );
  protected limit = this.supplierContractStore.limit;
  protected yearsError = this.supplierContractStore.yearsError;

  protected createError = this.parseRequestsStore.createError;

  protected activeParseRequests = computed(() =>
    (this.parseRequestsStore.parseRequests() ?? [])
      ?.filter(
        (parseRequest) =>
          parseRequest.data?.status === 'REQUESTED' || parseRequest.data?.status === 'IN_PROGRESS',
      )
      .filter((parseRequest) => this.parseRequestCreatedId() !== parseRequest.id),
  );
  protected parseRequestsLoading = this.parseRequestsStore.isLoading;
  protected parseRequestsLoadError = this.parseRequestsStore.error;

  protected allowContractAdd = computed(
    () => Object.keys(this.supplierContractStore.pages()).length > 0,
  );

  protected showLoadMorePlaceholders = computed(
    () =>
      !this.supplierContractStore.isLoading() && !this.supplierContractStore.areAllPagesPresent(),
  );

  constructor() {
    let previousFilter: SupplierContractsFilter | null = null;
    effect(() => {
      const yearsLoading = this.supplierContractStore.yearsLoading();
      const yearsError = this.yearsError();
      const years = this.supplierContractStore.years();
      if (yearsLoading || yearsError || years === null) {
        return;
      }
      const filter = this.supplierContractStore.filter();
      if (JSON.stringify(filter.data) !== JSON.stringify(previousFilter?.data)) {
        this.supplierContractStore.onChangeFilter();
      }
      previousFilter = filter;
    });

    effect(() => {
      const popover = this.tutorialPopover();
      if (popover && this.videoTutorialService.value()?.LumoTutorialSupplierContracts === false) {
        popover.showPopover();
        this.videoTutorialService.markTutorialAsViewed('LumoTutorialSupplierContracts');
      }
    });
  }

  ngOnInit(): void {
    this.initFilter();
    this.supplierContractStore.loadYears();
  }

  private initFilter() {
    const utilityType = this.activatedRoute.snapshot.queryParamMap.get('utilityType');
    const orderBy = this.activatedRoute.snapshot.queryParamMap.get('orderBy');
    const orderDirection = this.activatedRoute.snapshot.queryParamMap.get('orderDirection');
    const view = this.activatedRoute.snapshot.queryParamMap.get('view');
    const locationID = this.activatedRoute.snapshot.queryParamMap.get('locationID');
    const fullPrice = this.activatedRoute.snapshot.queryParamMap.get('fullPrice');
    const year = this.activatedRoute.snapshot.queryParamMap.get('year');

    const filterUpdate: SupplierContractsFilter = getInitialFilter();
    if (utilityType === 'ELECTRICITY' || utilityType === 'GAS') {
      filterUpdate.data['utilityType'] = utilityType;
    }
    if (
      orderBy === 'supplierName' ||
      orderBy === 'productName' ||
      orderBy === 'nextTerminationDate' ||
      orderBy === 'createdAt' ||
      orderBy === 'latestContractCheckAlertCount'
    ) {
      filterUpdate.data['orderBy'] = orderBy;
    }
    if (orderDirection === 'asc' || orderDirection === 'desc') {
      filterUpdate.data['orderDirection'] = orderDirection;
    }
    if (view === 'grid' || view === 'table') {
      filterUpdate['view'] = view;
    }
    if (locationID !== null) {
      filterUpdate.data['locationID'] = locationID;
    }

    if (fullPrice === 'true') {
      filterUpdate['fullPrice'] = true;
    }
    if (year && !isNaN(+year)) {
      filterUpdate.data['year'] = year;
    }

    this.supplierContractStore.filter.set(filterUpdate);
  }

  protected async onChangeFilter() {
    await new Promise((resolve) => setTimeout(resolve, 0)); // wait for form value to update
    const filterValue = this.supplierContractStore.filter();
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        view: filterValue.view || null,
        fullPrice: filterValue.fullPrice === true ? 'true' : null,
        ...filterValue.data,
        utilityType: filterValue.data.utilityType !== 'ALL' ? filterValue.data.utilityType : null,
        orderBy: filterValue.data.orderBy || null,
        orderDirection: filterValue.data.orderDirection || null,
        parseRequestCreated: this.parseRequestCreatedId() || null,
      },
      queryParamsHandling: 'replace',
    });
  }

  protected onChangeOrder(event: {
    orderBy: SupplierContractsFilter['data']['orderBy'];
    orderDirection: SupplierContractsFilter['data']['orderDirection'];
  }) {
    this.supplierContractStore.filter.update((current) => ({
      ...current,
      data: {
        ...current.data,
        ...event,
      },
    }));
    this.onChangeFilter();
  }

  protected onClickCloseCreateErrorAlert() {
    this.parseRequestsStore.clearCreateError();
  }

  protected onClickGoToParseRequestDetails(parseRequestId: string) {
    this.router.navigate([
      '/organizations',
      this.organizationId(),
      'parse-requests',
      parseRequestId,
    ]);
  }

  protected dismissParseRequestCreatedAlert() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        parseRequestCreated: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  protected onClickGoToPreviousYearContracts() {
    const years = this.supplierContractStore.years() ?? [];
    const currentYear = new Date().getFullYear();
    const previousYear = Math.max(...years.filter((year) => year < currentYear));
    this.supplierContractStore.filter.update((filter) => ({
      ...filter,
      data: {
        ...filter.data,
        year: `${previousYear}`,
      },
    }));
    this.onChangeFilter();
  }

  protected onClickGoToFutureYearContracts() {
    const years = this.supplierContractStore.years() ?? [];
    const currentYear = new Date().getFullYear();
    const nextYear = Math.min(...years.filter((year) => year > currentYear));
    this.supplierContractStore.filter.update((filter) => ({
      ...filter,
      data: {
        ...filter.data,
        year: `${nextYear}`,
      },
    }));
    this.onChangeFilter();
  }

  protected onLoadMore() {
    this.supplierContractStore.loadNextPage();
  }
}
